import {
  Box,
  Input,
  Textarea,
  Text,
  VStack,
  HStack,
  Flex,
  Button,
  Progress,
  FormControl,
  FormErrorMessage,
  Tooltip,
} from "@chakra-ui/react";
import { useMemo, useEffect, useState, useRef } from "react";
import { AddIcon } from "@chakra-ui/icons";
import VideoUpload from "../../common/VideoUpload";
import { CustomButton } from "@root/components/common/button/customButton";
import { useTranslation } from "react-i18next";
import { useCourseManagementContext } from "@root/context/course-management-context";
import { useParams } from "react-router-dom";
import {
  getFileSize,
  getVideoDuration,
  getVideoDurationFromUrl,
} from "@root/utils";
import { set } from "video.js/dist/types/tech/middleware";

type TouchedFieldsType = Record<
  string,
  { title?: boolean; description?: boolean }
>;

// Store saved URLs separately to track changes
type SavedUrlsType = Record<string, string>;

// Store saved chapter data to track changes
type SavedChaptersType = Record<string, { title: string; description: string }>;

const CourseStep2 = ({ defaultValues }: { defaultValues?: any }) => {
  const { t } = useTranslation("common");
  const {
    setIsStep2Disabled,
    addCourseChapter,
    addChapterLoading,
    currentCourse,
    chapters,
    setChapters,
    addVideoLoading,
    addCourseVideo,
    addCourseVideoChunked,
    updateCourseVideo,
    isUploadingChunks,
    uploadProgress,
    getCourseChapters,
    courseChaptersData,
    updateCourseId,
    updateCourseChapter,
    updateChapterLoading,
    updateVideoLoading,
    deleteCourseVideo,
    deleteVideoLoading,
    updateCourseVideoChunked,
    duration,
    setDuration
  } = useCourseManagementContext();
  const { id } = useParams();

  const [touchedFields, setTouchedFields] = useState<TouchedFieldsType>({});
  const [uploadingVideoId, setUploadingVideoId] = useState<string | null>(null);

  // Track upload in progress to prevent duplicate calls
  const uploadInProgressRef = useRef<Set<string>>(new Set());

  // Track saved URLs separately (key: `${chapterId}-${videoId}`, value: saved URL)
  const [savedUrls, setSavedUrls] = useState<SavedUrlsType>({});

  // Track saved video titles (key: `${chapterId}-${videoId}`, value: saved title)
  const [savedVideoTitles, setSavedVideoTitles] = useState<SavedUrlsType>({});

  // Track saved chapter data (key: chapterId, value: { title, description })
  const [savedChapters, setSavedChapters] = useState<SavedChaptersType>({});

  useEffect(() => {
    if (id) {
      getCourseChapters(Number(id));
    } else {
      setChapters([
        {
          id: Date.now().toString(),
          title: "",
          duration: "",
          description: "",
          videos: [],
        },
      ]);
    }
  }, [id]);

  // Reset uploadingVideoId when any upload completes
  // Only clear if upload is actually done AND we're not in the middle of duration calculation
  useEffect(() => {
    // Don't clear if upload is still in progress (check ref)
    const hasInProgressUploads = uploadInProgressRef.current.size > 0;

    if (
      !isUploadingChunks &&
      !addVideoLoading &&
      !updateVideoLoading &&
      uploadingVideoId &&
      !hasInProgressUploads // Only clear if no uploads are in progress
    ) {
      setUploadingVideoId(null);
      // Clear all in-progress uploads when loading completes
      uploadInProgressRef.current.clear();
    }
  }, [
    isUploadingChunks,
    addVideoLoading,
    updateVideoLoading,
    uploadingVideoId,
  ]);

  useEffect(() => {
    if (defaultValues && defaultValues.chapters?.length) {
      setChapters(defaultValues.chapters);
    } else if (!id) {
      setChapters([
        {
          id: Date.now().toString(),
          title: "",
          duration: "",
          description: "",
          videos: [],
        },
      ]);
    }
  }, [defaultValues, id]);

  // Update chapters when courseChaptersData changes
  useEffect(() => {
    if (courseChaptersData) {
      const newSavedUrls: SavedUrlsType = {};
      const newSavedVideoTitles: SavedUrlsType = {};
      const newSavedChapters: SavedChaptersType = {};

      const chaptersData = courseChaptersData?.rows?.map(
        (chapter: any, idx: number) => {
          // Store saved chapter data
          if (chapter?.id) {
            newSavedChapters[chapter.id] = {
              title: chapter?.title || "",
              description: chapter?.description || "",
            };
          }

          return {
            id: idx.toString(),
            title: chapter?.title,
            description: chapter?.description,
            chapterId: chapter?.id,
            order: chapter?.order,
            videos: chapter?.courseVideos?.map((video: any, idx2: number) => {
              const videoUrl = video?.isCustomUrl ? video?.url : "";

              // Store saved URL and title for this video
              if (video?.id) {
                const key = `${chapter?.id}-${video?.id}`;
                if (videoUrl) {
                  newSavedUrls[key] = videoUrl;
                }
                if (video?.title) {
                  newSavedVideoTitles[key] = video.title?.trim() || "";
                }
              }

              return {
                id: idx2.toString(),
                title: video?.title,
                uploadProgress: 0,
                isUploading: false,
                url: videoUrl,
                videoId: video?.id,
                size: video?.size,
                duration: video?.duration,
                isCustomUrl: video?.isCustomUrl,
                order: video?.order,
                urlBackup: video?.isCustomUrl ? null : video?.url,
                isPublic: Boolean(video?.isFree),
                transcodingStatus: video?.transcodingStatus,
              };
            }),
          };
        }
      );

      setSavedUrls(newSavedUrls);
      setSavedVideoTitles(newSavedVideoTitles);
      setSavedChapters(newSavedChapters);
      setChapters(chaptersData);
    }
  }, [courseChaptersData]);

  const isChapterFilled = (chapter: Chapter) => {
    return (
      chapter?.title?.trim() !== "" &&
      chapter?.description?.trim() !== "" &&
      chapter?.chapterId
    );
  };

  const isLatestVideoFilled = (chapter: Chapter) => {
    if (chapter.videos.length === 0) return true;
    const latestVideo = chapter.videos[chapter.videos.length - 1];
    return (
      latestVideo.title.trim() !== "" &&
      (latestVideo.file || latestVideo?.url || latestVideo?.urlBackup) &&
      latestVideo?.videoId
    );
  };

  const isCurrentChapterFilled = useMemo(() => {
    if (chapters.length === 0) return true;
    return isChapterFilled(chapters[chapters.length - 1]);
  }, [chapters]);

  const isSubmitEnabled = useMemo(() => {
    if (chapters.length === 1) {
      return isChapterFilled(chapters[0]);
    }
    return chapters.length > 1;
  }, [chapters]);

  useEffect(() => {
    setIsStep2Disabled(!isSubmitEnabled);
  }, [isSubmitEnabled]);

  // Check if chapter button should be disabled
  const isChapterButtonDisabled = (chapter: Chapter) => {
    // Must have title and description
    if (!chapter?.title?.trim() || !chapter?.description?.trim()) {
      return true;
    }

    // If chapter already saved (has chapterId)
    if (chapter?.chapterId) {
      const savedData = savedChapters[chapter.chapterId];
      if (savedData) {
        // Disable if nothing changed
        const titleSame = chapter.title?.trim() === savedData.title?.trim();
        const descSame =
          chapter.description?.trim() === savedData.description?.trim();
        return titleSame && descSame;
      }
    }

    // New chapter with title + description → ENABLED
    return false;
  };

  // Handle chapter update/create
  const handleChapterSubmit = (chapter: Chapter, chapterIndex: number) => {
    const payload = {
      courseId: updateCourseId ? updateCourseId : currentCourse?.id,
      title: chapter?.title,
      description: chapter?.description,
      order: chapter?.chapterId ? chapter?.order : chapterIndex,
    };

    // OPTIMISTIC UPDATE: Update savedChapters immediately
    if (chapter?.chapterId) {
      setSavedChapters((prev) => ({
        ...prev,
        [chapter.chapterId as number]: {
          title: chapter.title?.trim() || "",
          description: chapter.description?.trim() || "",
        },
      }));
      updateCourseChapter(chapter.chapterId, payload);
    } else {
      addCourseChapter(payload);
    }
  };

  const addChapter = () => {
    const newChapter: Chapter = {
      id: Date.now().toString(),
      title: "",
      duration: "",
      description: "",
      videos: [
        {
          id: Date.now().toString(),
          title: "",
          isPublic: true,
          uploadProgress: 0,
          isUploading: false,
        },
      ],
    };
    setChapters([...chapters, newChapter]);
  };

  const addVideo = (chapterId: number) => {
    const newVideo: Video = {
      id: Date.now().toString(),
      title: "",
      isPublic: false,
      uploadProgress: 0,
      isUploading: false,
      file: undefined,
    };
    setChapters(
      chapters?.map((chapter: Chapter) =>
        chapter?.chapterId === chapterId
          ? { ...chapter, videos: [...chapter.videos, newVideo] }
          : chapter
      )
    );
  };

  const updateChapter = (
    chapterId: string,
    field: keyof Chapter,
    value: string
  ) => {
    setChapters(
      chapters?.map((chapter: Chapter) =>
        chapter?.id === chapterId ? { ...chapter, [field]: value } : chapter
      )
    );
  };

  const updateVideo = (
    chapterId: number,
    videoId: string,
    field: keyof Video,
    value: any
  ) => {
    // Use functional update to ensure we always have the latest state
    setChapters((prevChapters: any[]) =>
      prevChapters?.map((chapter: any) =>
        Number(chapter?.chapterId) === Number(chapterId)
          ? {
              ...chapter,
              videos: chapter?.videos?.map((video: any) =>
                Number(video?.id) === Number(videoId)
                  ? { ...video, [field]: value }
                  : video
              ),
            }
          : chapter
      )
    );
  };

  const handleFileSelect = async (
    chapterId: number,
    videoId: string,
    file: File
  ) => {
    const duration = (await getVideoDuration(file)) || 0;
    setChapters((prevChapters: Chapter[]) =>
      prevChapters.map((chapter: Chapter) =>
        chapter.chapterId === chapterId
          ? {
              ...chapter,
              videos: chapter.videos.map((video) =>
                Number(video.id) === Number(videoId)
                  ? {
                      ...video,
                      file: file,
                      isUploading: true,
                      uploadProgress: 0,
                      size: getFileSize(file),
                      duration: duration || 0,
                    }
                  : video
              ),
            }
          : chapter
      )
    );

    const interval = setInterval(() => {
      setChapters((prevChapters: Chapter[]) =>
        prevChapters.map((chapter) =>
          chapter.chapterId === chapterId
            ? {
                ...chapter,
                videos: chapter.videos.map((video) =>
                  video.id === videoId
                    ? {
                        ...video,
                        uploadProgress: Math.min(
                          (video.uploadProgress || 0) + 10,
                          100
                        ),
                      }
                    : video
                ),
              }
            : chapter
        )
      );
    }, 200);

    setTimeout(() => {
      clearInterval(interval);
      setChapters((prevChapters: Chapter[]) =>
        prevChapters.map((chapter) =>
          chapter.chapterId === chapterId
            ? {
                ...chapter,
                videos: chapter.videos.map((video) =>
                  video.id === videoId
                    ? {
                        ...video,
                        isUploading: false,
                        uploadProgress: 100,
                      }
                    : video
                ),
              }
            : chapter
        )
      );
    }, 2000);
  };

  const handleVideoUpdate = (
    chapterId: number,
    videoId: string,
    field: string,
    value: any
  ) => {
    // Update the field immediately (synchronous) using functional update
    updateVideo(chapterId, videoId, field as keyof Video, value);

    // If URL is being updated, reset duration to 0 so it will be calculated on button click
    // Don't calculate duration here to avoid duplicate API calls
    if (field === "url" && value && value.trim()) {
      // Reset duration when URL changes so it will be recalculated on upload
      updateVideo(chapterId, videoId, "duration" as keyof Video, 0);
    }
  };

  const handleVideoFileSelect = (
    chapterId: number,
    videoId: string,
    file: File
  ) => {
    handleFileSelect(chapterId, videoId, file);
  };

  const handleSubmit = () => {
    const formattedData = chapters.map((chapter: Chapter) => ({
      ...chapter,
      videos: chapter.videos.map((video) => ({
        title: video.title,
        isPublic: video.isPublic,
        file:
          video.file !== undefined
            ? {
                name: video.file.name,
                size: video.file.size,
                type: video.file.type,
              }
            : null,
      })),
    }));
  };

  // Check if video button should be disabled
  const isVideoButtonDisabled = (video: any, chapterId: number | undefined) => {
    // Must have title
    if (!video?.title?.trim()) return true;

    // Must have file or url or urlBackup (for uploaded videos)
    if (!video?.file && !video?.url?.trim() && !video?.urlBackup?.trim())
      return true;

    // If video already saved (has videoId)
    if (video?.videoId) {
      const savedKey = `${chapterId}-${video?.videoId}`;
      const savedTitle = savedVideoTitles[savedKey] || "";
      const savedUrl = savedUrls[savedKey] || "";

      // Has new file selected → ENABLED
      if (video?.file) {
        return false;
      }

      // Check if title changed → ENABLED
      if (video?.title?.trim() !== savedTitle?.trim()) {
        return false;
      }

      // URL type: check if URL changed from saved
      if (video?.url?.trim()) {
        return video?.url?.trim() === savedUrl?.trim(); // Disable if same
      }

      // Has urlBackup (uploaded video) but no title/URL change → Check if we need to allow title updates
      if (video?.urlBackup?.trim()) {
        // If title changed, enable button
        if (video?.title?.trim() !== savedTitle?.trim()) {
          return false;
        }
        // Otherwise, disable (no changes)
        return true;
      }

      // No file and no URL → DISABLED
      return true;
    }

    // New video (no videoId) with title + (file or url) → ENABLED
    return false;
  };

  // Handle video upload with tracking
  const handleVideoUpload = async (
    chapter: any,
    video: any,
    videoIndex: number
  ) => {
    // Prevent duplicate calls - check if upload is already in progress
    const uploadKey = `${chapter?.chapterId}-${video.id}`;
    if (uploadInProgressRef.current.has(uploadKey)) {
      return;
    }

    // Mark upload as in progress
    uploadInProgressRef.current.add(uploadKey);

    // Set loading state immediately to show loader and block button
    const videoIdString = String(video.id);
    setUploadingVideoId(videoIdString); // Ensure it's a string for consistent comparison

    // If it's a URL video and duration is 0 or missing, calculate it FIRST
    // CRITICAL: Wait for duration calculation to complete (success or error) before proceeding
    let duration = video?.duration || 0;
    if (video?.url?.trim() && !video?.file && (!duration || duration === 0)) {
      try {
        setDuration(true)
        // Create a promise with timeout wrapper
        const durationWithTimeout = new Promise<number>((resolve) => {

          const timeout = setTimeout(() => {
            resolve(0); // Resolve with 0 on timeout
          }, 300000); // 5 minute timeout (300 seconds)

          // Call the actual duration function
          getVideoDurationFromUrl(video.url.trim())
            .then((calculatedDuration) => {
              clearTimeout(timeout);
              resolve(calculatedDuration || 0);
            })
            .catch((error) => {
              clearTimeout(timeout);
              resolve(0); // Resolve with 0 on error
            });
        });

        // WAIT for duration calculation to complete before proceeding
        duration = await durationWithTimeout;

        // Update the video object with calculated duration for UI
        if (duration > 0) {
          updateVideo(
            chapter?.chapterId || 0,
            video.id,
            "duration" as keyof Video,
            duration
          );
        }
      } catch (error) {
        // Continue with duration 0 if calculation fails
        duration = 0;
      }
    }

    const videoData: any = {
      order: video?.videoId ? video?.order : videoIndex,
      title: video?.title,
      size: video?.size || 0,
      duration: duration, // Use the calculated duration (or 0 if not available)
      isFree: video?.isPublic ? true : false,
    };

    // Ensure duration is always a number (not undefined)
    if (typeof videoData.duration !== "number") {
      videoData.duration = 0;
    }

    if (video?.file) {
      // New file upload or updating with new file
      videoData.file = video?.file;
      videoData.mimetype = video?.file?.type;
      videoData.filename = video?.file?.name;
      videoData.isCustomUrl = false;
    } else if (video?.url?.trim()) {
      // Custom URL video (new or existing)
      videoData.url = video?.url?.trim();
      videoData.isCustomUrl = true;
      videoData.mimetype = "custom";
    } else if (video?.videoId && video?.urlBackup?.trim()) {
      // Existing uploaded video being updated without new file
      videoData.url = video?.urlBackup?.trim();
      videoData.isCustomUrl = false;
      videoData.mimetype = "video/mp4"; // Default mimetype for uploaded videos
    }

    // OPTIMISTIC UPDATE: Update savedUrls and savedVideoTitles immediately
    if (video?.videoId) {
      const key = `${chapter?.chapterId}-${video?.videoId}`;

      // Update saved URL if it's a URL update (no file)
      if (video?.url && !video?.file) {
        setSavedUrls((prev) => ({
          ...prev,
          [key]: video?.url?.trim(),
        }));
      }

      // Always update saved title
      setSavedVideoTitles((prev) => ({
        ...prev,
        [key]: video?.title?.trim() || "",
      }));
    }

    // Make the API call
    try {
      if (video?.videoId) {
        if (video?.file) {
          await updateCourseVideoChunked(
            updateCourseId ?? currentCourse?.id,
            chapter?.chapterId,
            video?.videoId,
            videoData
          );
        } else {
          await updateCourseVideo(
            updateCourseId ?? currentCourse?.id,
            chapter?.chapterId,
            video?.videoId,
            videoData
          );
        }
      } else {
        if (video?.file) {
          await addCourseVideoChunked(
            updateCourseId ?? currentCourse?.id,
            chapter?.chapterId,
            videoData
          );
        } else {
          await addCourseVideo(
            updateCourseId ?? currentCourse?.id,
            chapter?.chapterId,
            videoData
          );
        }
      }
    } catch (error) {
      // Re-throw to let the error handler know
      throw error;
    } finally {
      // Remove from in-progress set when done (success or error)
      // Note: Don't clear uploadingVideoId here - let the useEffect handle it
      // when the actual loading states (addVideoLoading, updateVideoLoading) become false
      uploadInProgressRef.current.delete(uploadKey);
      setDuration(false)
    }
  };

  const renderVideoUploader = (chapter: Chapter) => {
    return (
      <Box>
        <VStack spacing={4} align="stretch">
          {chapter?.videos?.map((video, videoIndex) => (
            <Box key={video.id}>
              <VideoUpload
                video={video}
                videoIndex={videoIndex}
                onVideoUpdate={(videoId: string, field: string, value: any) =>
                  handleVideoUpdate(
                    chapter.chapterId || 0,
                    videoId,
                    field,
                    value
                  )
                }
                onVideoRemove={(videoId: number) =>
                  deleteCourseVideo(chapter?.chapterId, videoId)
                }
                onFileSelect={(videoId: string, file: File) =>
                  handleVideoFileSelect(chapter.chapterId || 0, videoId, file)
                }
                setChapters={setChapters}
                deleteLoading={deleteVideoLoading}
              />

              <Flex justifyContent={"space-between"} gap={"50px"}>
                {isUploadingChunks &&
                uploadProgress > 0 &&
                uploadingVideoId === video.id ? (
                  <Box width="100%" mt={2}>
                    <Text fontSize="sm" mb={1}>
                      Uploading: {uploadProgress}%
                    </Text>
                    <Progress
                      value={uploadProgress}
                      colorScheme="blue"
                      size="sm"
                      borderRadius="md"
                    />
                  </Box>
                ) : (
                  <Box />
                )}
                <Tooltip
                  label={
                    !video?.title?.trim()
                      ? t("courseManagement.enterVideoTitle")
                      : ""
                  }
                  isDisabled={!!video?.title?.trim()}
                  hasArrow
                  placement="top"
                >
                  <Box display="inline-block">
                    <Button
                      type="button"
                      size="md"
                      mt={"14px"}
                      alignSelf="flex-end"
                      bgColor={"#1819B4"}
                      fontSize={"14px"}
                      fontWeight={500}
                      onClick={() =>
                        handleVideoUpload(chapter, video, videoIndex)
                      }
                      isDisabled={
                        isVideoButtonDisabled(video, chapter?.chapterId) ||
                        uploadingVideoId === String(video.id) ||
                        uploadingVideoId === video.id
                      }
                      isLoading={
                        uploadingVideoId === String(video.id) ||
                        uploadingVideoId === video.id
                      }
                    >
                      {video?.videoId
                        ? t("courseManagement.updateVideo")
                        : t("courseManagement.saveVideo")}
                    </Button>
                  </Box>
                </Tooltip>
              </Flex>
            </Box>
          ))}
        </VStack>
      </Box>
    );
  };

  const renderChapter = () => {
    return (
      <VStack
        border="1px solid #CDD6DC"
        borderRadius={"20px"}
        spacing={6}
        align="stretch"
      >
        {chapters?.map((chapter: Chapter, chapterIndex: number) => (
          <Box
            key={chapter.id}
            borderRadius="20px"
            p={6}
            borderLeft="4px solid"
            borderLeftColor={"#1819B4"}
          >
            <Text fontSize="20px" textColor={"#2F2F34"} fontWeight="600" mb={4}>
              {t("course.chapter", { chapterNumber: chapterIndex + 1 })}
            </Text>
            <HStack spacing={4} mb={4}>
              <Box flex={1}>
                <Text
                  fontSize="16px"
                  textColor={"#2F2F34"}
                  fontWeight="600"
                  mb={2}
                >
                  {t("course.chapterTitle")}
                </Text>
                <FormControl
                  isInvalid={
                    touchedFields[chapter?.id]?.title && !chapter?.title?.trim()
                  }
                >
                  <Input
                    borderRadius={"8px"}
                    border={"1px solid"}
                    borderColor={"#CDD6DC"}
                    _hover={{ borderColor: "#CDD6DC" }}
                    _focus={{ borderColor: "#CDD6DC" }}
                    h={"49px"}
                    placeholder={t("course.enterChapterTitle")}
                    value={chapter?.title}
                    onChange={(e) =>
                      updateChapter(chapter?.id, "title", e.target.value)
                    }
                    onBlur={() =>
                      setTouchedFields((prev: TouchedFieldsType) => ({
                        ...prev,
                        [chapter?.id]: { ...prev[chapter?.id], title: true },
                      }))
                    }
                  />
                  <FormErrorMessage>
                    {t("course.chapterTitleRequired")}
                  </FormErrorMessage>
                </FormControl>
              </Box>
            </HStack>

            <Box mb={6}>
              <Text
                fontSize="16px"
                textColor={"#2F2F34"}
                fontWeight="600"
                mb={2}
              >
                {t("course.chapterDescription")}
              </Text>
              <FormControl
                isInvalid={
                  touchedFields[chapter?.id]?.description &&
                  !chapter?.description?.trim()
                }
              >
                <Textarea
                  borderRadius={"8px"}
                  border={"1px solid"}
                  borderColor={"#CDD6DC"}
                  _hover={{ borderColor: "#CDD6DC" }}
                  _focus={{ borderColor: "#CDD6DC" }}
                  placeholder={t("course.chapterDescriptionPlaceholder")}
                  bgColor={"#fff"}
                  value={chapter?.description}
                  onChange={(e) =>
                    updateChapter(chapter?.id, "description", e.target.value)
                  }
                  onBlur={() =>
                    setTouchedFields((prev: TouchedFieldsType) => ({
                      ...prev,
                      [chapter?.id]: {
                        ...prev[chapter?.id],
                        description: true,
                      },
                    }))
                  }
                  rows={3}
                />
                <FormErrorMessage>
                  {t("course.chapterDescriptionRequired")}
                </FormErrorMessage>
              </FormControl>
            </Box>

            <Flex justifyContent={"flex-end"}>
              <Button
                type="button"
                size="md"
                alignSelf="flex-end"
                bgColor={"#1819B4"}
                fontSize={"14px"}
                fontWeight={500}
                onClick={() => handleChapterSubmit(chapter, chapterIndex)}
                isDisabled={isChapterButtonDisabled(chapter)}
                isLoading={addChapterLoading || updateChapterLoading}
              >
                {chapter?.chapterId
                  ? t("courseManagement.updateChapter")
                  : t("courseManagement.createChapter")}
              </Button>
            </Flex>

            {chapter?.chapterId && (
              <Box mt={"30px"}>
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontSize="16px" textColor={"#2F2F34"} fontWeight="600">
                    {t("course.chapterVideos")}
                  </Text>

                  <CustomButton
                    text={t("course.addNewVideo")}
                    leftIcon={<AddIcon />}
                    onClick={() =>
                      chapter?.chapterId && addVideo(chapter?.chapterId)
                    }
                    color="#1819B4"
                    border={"1px solid #1819B4"}
                    fontSize="14px"
                    fontWeight={500}
                    borderColor="#1819B4"
                    borderRadius="8px"
                    borderWidth="1px"
                    isDisabled={!isLatestVideoFilled(chapter)}
                  />
                </Flex>

                {renderVideoUploader(chapter)}
              </Box>
            )}
          </Box>
        ))}
      </VStack>
    );
  };

  return (
    <Box p={6} maxW="1200px" mx="auto">
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="24px" fontWeight="600" color="#2F2F34">
          {t("course.addChaptersAndVideos")}
        </Text>

        <CustomButton
          text={t("course.addNewChapter")}
          leftIcon={<AddIcon />}
          onClick={addChapter}
          color="#1819B4"
          border={"1px solid #1819B4"}
          fontSize="14px"
          fontWeight={500}
          borderColor="#1819B4"
          borderRadius="8px"
          borderWidth="1px"
          isDisabled={!isCurrentChapterFilled}
        />
      </Flex>
      {renderChapter()}
    </Box>
  );
};

export default CourseStep2;
